import { ChangeEvent, FocusEvent, KeyboardEvent, useEffect, useState } from 'react';
import { faCalendarAlt } from '@fortawesome/pro-duotone-svg-icons/faCalendarAlt';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

import { daysOfWeek } from 'us-react-components/dist/constants/DaysOfWeek';
import { timesOfDay } from 'us-react-components/dist/constants/Times';
import { CustomCheckbox } from 'us-react-components/dist/CustomCheckbox/CustomCheckbox';
import { DayPicker } from 'us-react-components/dist/DayPicker/DayPicker';
import { Select } from 'us-react-components/dist/Select/Select';
import { TimePicker } from 'us-react-components/dist/TimePicker/TimePicker';
import { ToggleSwitch } from 'us-react-components/dist/ToggleSwitch/ToggleSwitch';
import { DateFormat, formatDateFromString, formatDateToString, getShortWeekdayString } from 'us-react-components/dist/utils/date';
import {
  DayOfWeekOption,
  JobAvailabilityType,
  TimeOfDayOption,
  RecurringDay,
  SavedSearch,
  SavedSearchService,
  SitterSearchRecurring,
} from 'us-web-services';
import { NuxContainer } from 'us-react-components/dist/NuxContainer/NuxContainer';
import { Button, ButtonType } from '../../../components/Button';
import { useDependencies } from '../../../dependencies';
import { Routes } from '../../../Routes';
import { IconText } from './components/IconText';


const DAYS_IN_WEEK = 7;
const INTERVAL_MINUTES = 15;
const ONE_HOUR_MINUTES = 60;
const ONE_DAY_MS = 86400000;
const FOUR_HOURS_MS = 14400000;
const SPECIFIC_TIME_CODE = 'specific';

const dayLabelClasses = 'text-sm font-semibold mt-[10px] mr-[2px] text-right';

export const SelectDateTimes = observer((): JSX.Element => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { services: { tracker }, stores: { familyRegistration } } = useDependencies();
  const { startDate, endDate, recurring } = familyRegistration;

  const isSameDay = (date: Date, otherDate?: Date): boolean => !otherDate || (date.getMonth() === otherDate.getMonth() && date.getDate() === otherDate.getDate());
  const [isOvernightEnabled, setIsOvernightEnabled] = useState<boolean>(!!endDate || (!!startDate && !isSameDay(startDate, endDate)));
  const isRecurring = familyRegistration.availabilityType === JobAvailabilityType.partTime || familyRegistration.availabilityType === JobAvailabilityType.fullTime;

  const [timeOfDaySelect, setTimeOfDaySelect] = useState<TimeOfDayOption>(recurring?.timeOfDay ?? { code: '', label: '', id: -1 });
  const [validDays, setValidDays] = useState<boolean[]>([]);

  const isSpecificTime = recurring?.timeOfDay?.code === SPECIFIC_TIME_CODE;

  const toggleFlexibleDays = (): void => {
    familyRegistration.setRecurring({ ...recurring, flexible: !recurring?.flexible });
  };

  const updateValidDays = (start?: Date, end?: Date): void => {
    const numDays = (!start || !end)
      ? DAYS_IN_WEEK
      : Math.min(Math.ceil((end.getTime() - start.getTime()) / ONE_DAY_MS), DAYS_IN_WEEK);
    const firstDayCode = start ? start.getDay() : 0;

    const updatedValidDays: boolean[] = [];
    for (let i = 0; i < numDays; i++) {
      const dayCode = (firstDayCode + i) % 7;
      updatedValidDays[dayCode] = true;
    }

    let days: RecurringDay[] = [];
    if (recurring?.days?.length) {
      days = [...recurring.days];
      recurring.days.forEach((day: RecurringDay) => {
        if (!updatedValidDays[day.weekday.day]) {
          const invalidIndex = days.findIndex((d: RecurringDay) => d.weekday.day === day.weekday.day);
          days.splice(invalidIndex, 1);
        }
      });
    } else if (familyRegistration.availabilityType === JobAvailabilityType.fullTime) {
      updatedValidDays.forEach((day: boolean, index: number) => {
        const existingDay = days.findIndex((d: RecurringDay) => d.weekday.day === index);
        // pre-select new weekdays for full-time jobs if not already selected
        if (day && existingDay === -1 && index > 0 && index < 6) {
          days.push({
            weekday: {
              ...daysOfWeek[index],
              selected: true,
            },
          });
        }
      });
    }
    familyRegistration.setRecurring({ ...recurring, days });

    setValidDays(updatedValidDays);
  }
  const updateSearch = async (sitterSearchRecurrings: SitterSearchRecurring[]): Promise<void> => {
    try {
      const savedSearch: SavedSearch = {
        id: familyRegistration.savedSearchId ?? undefined,
        recurring: sitterSearchRecurrings
      };

      if (familyRegistration.savedSearchId) {
        await SavedSearchService.updateSearch(familyRegistration.savedSearchId, savedSearch);
      }
    } catch {
      toast.error(t('pages.nux.family.dateTimes.errorUpdatingSearch'));
    }
  };

  const navigateNextPage = async (): Promise<void> => {
      navigate(Routes.parent.registration.fantastic);
  };

  const handleContinue = (): void => {
    const sitterSearchRecurrings: SitterSearchRecurring[] = [];
    if (isRecurring) {
      if (!recurring?.days?.length) {
        return;
      }

      const days = [...recurring.days];
      if (!isSpecificTime) {
        days.forEach((day: RecurringDay) => {
          day.startTime = undefined;
          day.endTime = undefined;
          if (recurring?.timeOfDay?.id) {
            const sitterSearchRecurring: SitterSearchRecurring = {
              dayOfWeek: {
                id: day.weekday.id,
                code: day.weekday.code,
                label: day.weekday.label,
              },
              timeOfDayIds: [recurring.timeOfDay.id],
            };

            sitterSearchRecurrings.push(sitterSearchRecurring);
          };
        });
      }
      familyRegistration.setRecurring({
        ...recurring,
        days,
      });
    } else {
      familyRegistration.setRecurring(undefined);
    }

    updateSearch(sitterSearchRecurrings);

    navigateNextPage();
  };

  const isContinueDisabled = (): boolean => {
    if (isRecurring) {
      return timeOfDaySelect?.id === -1 || !recurring?.days?.length ||
        (isSpecificTime && recurring?.days?.find((day: RecurringDay) => !day.startTime || !day.endTime) !== undefined);
    }

    return !startDate || (!familyRegistration.startTime || !familyRegistration.endTime);
  };

  const isDayDisabled = (day: DayOfWeekOption): boolean => {
    if (!startDate || !endDate) {
      return false;
    }

    return validDays[day.day] !== true;
  };

  const isDaySelected = (targetDay: DayOfWeekOption): boolean => !!recurring?.days?.find((day: RecurringDay) => day.weekday.id === targetDay.id);

  const getTitle = (): string => {
    if (startDate) {
      return isRecurring ? t('pages.nux.family.dateTimes.recurring.daysOfWeekTitle') : t('pages.nux.family.dateTimes.specific.timeTitle')
    }

    return t(`pages.nux.family.dateTimes.${isRecurring ? 'recurring' : 'specific'}.dateTitle`);
  };

  const getStartDateString = (): string | undefined =>
    startDate ? formatDateToString(startDate, DateFormat.DATE_SHORT_FORMAT_SLASHES) : undefined;
  const getEndDateString = (): string | undefined =>
    endDate ? formatDateToString(endDate, DateFormat.DATE_SHORT_FORMAT_SLASHES) : undefined;

  const getStartDay = (): string | undefined =>
    startDate ? getShortWeekdayString(startDate).toUpperCase() : undefined;
  const getEndDay = (): string | undefined =>
    endDate ? getShortWeekdayString(endDate).toUpperCase() : undefined;

  const getMinStartTime = (targetStartDate?: Date): Date => {
    const now = new Date();
    const start = targetStartDate || startDate;

    if (isSameDay(now, start)) {
      return new Date(now.setMinutes((Math.ceil(now.getMinutes() / INTERVAL_MINUTES) * INTERVAL_MINUTES) % ONE_HOUR_MINUTES));
    }

    return new Date(now.setHours(0, 0, 0, 0));
  };

  const getMinEndTime = (date: Date | undefined, startTime: string | undefined, end?: Date): Date => {
    if (end && date?.getDate() !== end.getDate()) {
      return new Date(end.setHours(0, 0, 0, 0));
    }

    if (date && startTime) {
      const startDateTime = date;
      const [time, period] = startTime.split(' ');
      const [hour, minute] = time.split(':');
      let formattedHour = parseInt(hour, 10);
      const formattedMinute = parseInt(minute, 10);

      if (period === 'PM') {
        formattedHour += 12;
      } else if (period === 'AM' && formattedHour === 12) {
        formattedHour = 0;
      }

      startDateTime.setHours(formattedHour);
      startDateTime.setMinutes(formattedMinute);

      const addedTime = startDateTime.getTime() + (INTERVAL_MINUTES * 60000);

      return new Date(startDateTime.setTime(addedTime));
    }

    return date ? new Date(date.getTime()) : new Date();
  };

  const validateDateChange = (date: Date, isEndDate?: boolean): void => {
    const newDate = isEndDate ? new Date(date.setHours(23, 59, 59, 999)) : new Date(date.setHours(0, 0, 0, 0));

    if (startDate && endDate) {
      if (isEndDate && newDate < startDate) {
        toast.error(t('validation.errors.endDateBeforeStartDate'));
        familyRegistration.setStartDate(undefined);
        familyRegistration.setEndDate(undefined);

        if (!isRecurring) {
          familyRegistration.setStartTime('');
          familyRegistration.setEndTime('');
          setIsOvernightEnabled(false);
        }
      } else if (!isEndDate && newDate > endDate) {
        familyRegistration.setEndDate(undefined);
      }
    }

    let start = startDate;
    let end = endDate;

    if (isEndDate) {
      end = newDate;
      familyRegistration.setEndDate(end);
    } else {
      start = newDate;
      familyRegistration.setStartDate(start);
    }

    if (isRecurring) {
      updateValidDays(start, end);
    } else if (start && isSameDay(start, end) && familyRegistration.startTime && familyRegistration.endTime
      && familyRegistration.startTime >= familyRegistration.endTime) {
      familyRegistration.setEndTime('');
    }

    const minStart = formatDateToString(getMinStartTime(start), DateFormat.API_TIME_FORMAT);
    if (familyRegistration.startTime && familyRegistration.startTime < minStart) {
      familyRegistration.setStartTime(minStart);
    }
  };

  const enableOvernightOption = (clearEndTime = true): void => {
    const start = startDate ?? new Date();
    const newEnd = new Date(new Date().setDate(start.getDate() + 1));

    tracker.track('clicked-button', {
      elementText: t('pages.nux.family.dateTimes.specific.overnightOption')
    });

    familyRegistration.setEndDate(newEnd);

    if (clearEndTime) {
      familyRegistration.setEndTime('');
    }

    setIsOvernightEnabled(true);
  };

  const validateTimeChange = (time: Date, isEndTime?: boolean): void => {
    if (time.getMinutes() % INTERVAL_MINUTES !== 0) {
      if (isEndTime) {
        familyRegistration.setEndTime('');
      } else {
        familyRegistration.setStartTime('');
      }
      return;
    }

    const sameDay = !startDate || isSameDay(startDate, endDate);
    const timeString = formatDateToString(time, DateFormat.API_TIME_FORMAT);

    if (isEndTime) {
      if (sameDay && familyRegistration.startTime && timeString <= familyRegistration.startTime) {
        familyRegistration.setEndTime('');
        return;
      }
      familyRegistration.setEndTime(timeString);
    } else {
      if (sameDay) {
        const startDatetime = startDate ? new Date(startDate) : new Date();
        const minEndDatetime = getMinEndTime(startDatetime, timeString);
        const minEndTime = formatDateToString(minEndDatetime, DateFormat.API_TIME_FORMAT);

        const defaultEndDatetime = new Date(startDatetime);
        defaultEndDatetime.setHours(time.getHours());
        defaultEndDatetime.setMinutes(time.getMinutes())
        defaultEndDatetime.setTime(defaultEndDatetime.getTime() + FOUR_HOURS_MS);

        const defaultEndTime = formatDateToString(defaultEndDatetime, DateFormat.API_TIME_FORMAT);

        if (!familyRegistration.endTime
          || familyRegistration.endTime < minEndTime
          || timeString >= familyRegistration.endTime) {
          familyRegistration.setEndTime(defaultEndTime);

          if (defaultEndDatetime.getDate() > startDatetime.getDate()) {
            enableOvernightOption(false); // enable overnight option when default end time set past midnight
          }
        }
      }

      familyRegistration.setStartTime(timeString);
    }
  };

  const onDayOfWeekChange = (e: ChangeEvent<HTMLInputElement>, dayId: number): void => {
    const selectedDayIndex = daysOfWeek.findIndex((day: DayOfWeekOption) => day.id === dayId);
    const selectedDay = daysOfWeek[selectedDayIndex];

    let days = [...recurring?.days || []];

    if (e.currentTarget.checked) {
      days.push({
        weekday: {
          ...selectedDay,
          selected: true,
        },
      });

      tracker.track('clicked-button', {
        elementText: selectedDay.label,
        elementId: selectedDay.id,
      });
    } else {
      days = days.filter(day => day.weekday.id !== dayId);
    }
    familyRegistration.setRecurring({ ...recurring, days });
  };

  const onTimeOfDaySelect = (e: ChangeEvent<HTMLSelectElement>): void => {
    const timeOfDayIndex = timesOfDay.findIndex((time: TimeOfDayOption) => time.id.toString() === e.target.value);

    setTimeOfDaySelect(timesOfDay[timeOfDayIndex]);
    familyRegistration.setRecurring({ ...recurring, timeOfDay: timesOfDay[timeOfDayIndex] });

    tracker.track('clicked-button', {
      elementText: timesOfDay[timeOfDayIndex].label,
      elementId: e.target.value,
    });
  };

  const copyStartAndEndTimes = (sourceDay: RecurringDay): void => {
    const days = [...recurring?.days || []];
    days.forEach((day: RecurringDay) => {
      if (day.weekday.id !== sourceDay.weekday.id && !day.startTime && !day.endTime) {
        day.startTime = sourceDay.startTime;
        day.endTime = sourceDay.endTime;
      }
    });

    familyRegistration.setRecurring({ ...recurring, days });
  };

  const getMaxEndTime = (): Date => new Date(new Date().setHours(23, 59, 59));

  const getTimeApiString = (date: Date): string => formatDateToString(date, DateFormat.API_JOB_DAY_TIME_FORMAT);

  const addRecurringTime = (datetime: Date, dayId: number, isStart: boolean): void => {
    const apiTimeString = getTimeApiString(datetime);
    const selectedDay = daysOfWeek[daysOfWeek.findIndex((day: DayOfWeekOption) => day.id === dayId)];
    const existingDay = recurring?.days?.findIndex((day: RecurringDay) => day.weekday.id === dayId) ?? -1;
    const days = [...recurring?.days || []];
    const selectedTimes = {
      weekday: {
        ...selectedDay,
        selected: true,
      },
      startTime: isStart ? apiTimeString : (days[existingDay]?.startTime || ''),
      endTime: !isStart ? apiTimeString : (days[existingDay]?.endTime || ''),
    }

    if (isStart && (!selectedTimes.endTime || selectedTimes.startTime >= selectedTimes.endTime)) {
      const newEndTime = new Date(datetime);
      newEndTime.setTime(newEndTime.getTime() + FOUR_HOURS_MS);

      selectedTimes.endTime = getTimeApiString(newEndTime);
    }

    if (existingDay !== -1) {
      days[existingDay] = selectedTimes;
      if ((isStart && days[existingDay].endTime || !isStart && days[existingDay].startTime)) {
        copyStartAndEndTimes(days[existingDay]);
      }
    } else {
      days.push(selectedTimes);
    }

    familyRegistration.setRecurring({ ...recurring, days });
  };

  const getRecurringMinEndTime = (dayId: number): Date => {
    const dayIndex = recurring?.days?.findIndex((day: RecurringDay) => day.weekday.id === dayId) ?? -1;
    const days = [...recurring?.days ?? []];

    if (days[dayIndex].startTime) {
      return getMinEndTime(new Date(), days[dayIndex].startTime);
    }

    return new Date();
  };

  const getSpecificDayStartTime = (dayId: number): string | undefined => {
    const dayIndex = recurring?.days?.findIndex((day: RecurringDay) => day.weekday.id === dayId) ?? -1;

    if (dayIndex !== -1 && recurring?.days && recurring.days[dayIndex].startTime) {
      const currentDate = formatDateToString(new Date(), DateFormat.API_DATE_FORMAT);
      const dateString = `${currentDate} ${recurring.days[dayIndex].startTime}`;

      return formatDateFromString(dateString, DateFormat.TIME_DISPLAY_FORMAT);
    }

    return undefined;
  };

  const getSpecificDayEndTime = (dayId: number): string | undefined => {
    const dayIndex = recurring?.days?.findIndex((day: RecurringDay) => day.weekday.id === dayId) ?? -1;

    if (dayIndex !== -1 && recurring?.days && recurring.days[dayIndex].endTime) {
      const currentDate = formatDateToString(new Date(), DateFormat.API_DATE_FORMAT);
      const dateString = `${currentDate} ${recurring.days[dayIndex].endTime}`;

      return formatDateFromString(dateString, DateFormat.TIME_DISPLAY_FORMAT);
    }

    return undefined;
  };

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>): void => {
    if (e.key === 'Enter') {
      e.stopPropagation();
    }
  };

  familyRegistration.setCurrentRoute(Routes.parent.registration.selectDateTimes);

  useEffect(() => {
    updateValidDays(startDate, endDate);
  }, []);

  return (
    <NuxContainer
      familyRegistration={familyRegistration}
      navigate={navigate}
      tracker={tracker}
      title={getTitle()}
      onContinue={handleContinue}
      isContinueDisabled={isContinueDisabled()}>
      <div className='pl-4 pr-3'>
        <div className='w-full flex font-semibold justify-center mt-8 mb-3'>
          <IconText
            classNames='min-w-[300px] justify-center'
            icon={faCalendarAlt}
            text={startDate ? `${getStartDateString()}${(endDate
              && !isSameDay(startDate, endDate)) ? ` - ${getEndDateString()}` : ''}`
              : t('pages.nux.family.dateTimes.specific.selectDate')} />
        </div>
        <div className='w-full flex pb-12 justify-center'>
          <DayPicker
            minDate={new Date()}
            onChange={(date: Date): void => validateDateChange(date, false)}
            onInputClick={(): void => {
              tracker.track('clicked-button', {
                elementText: t('pages.nux.family.dateTimes.startDate'),
              });
            }}
            onKeyDown={onKeyDown}
            placeholderText={t('pages.nux.family.dateTimes.startDate')}
            startOpen={startDate === undefined}
            value={startDate?.toDateString()}
          />
          {(isOvernightEnabled || isRecurring) && (
            <DayPicker
              classNames='ml-4'
              minDate={startDate || new Date()}
              onChange={(date: Date): void => validateDateChange(date, true)}
              onInputClick={(): void => {
                tracker.track('clicked-button', {
                  elementText: t('pages.nux.family.dateTimes.endDate'),
                });
              }}
              onKeyDown={onKeyDown}
              placeholderText={t('pages.nux.family.dateTimes.endDate')}
              value={endDate?.toDateString()}
            />
          )}
        </div>
        {!!startDate && !isRecurring && (
          <div className='mb-6'>
            <div className='w-full flex justify-center'>
              <div className='flex w-1/2'>
                <div className={`${dayLabelClasses} w-[34px]`}>
                  {getStartDay()}
                </div>
                <TimePicker
                  maxTime={getMaxEndTime()}
                  minTime={getMinStartTime()}
                  onChange={(time: Date): void => validateTimeChange(time, false)}
                  onFocus={(e: FocusEvent<HTMLInputElement>): void => e.target.blur()}
                  onInputClick={(): void => {
                    tracker.track('clicked-button', {
                      elementText: t('pages.nux.family.dateTimes.startTime'),
                    });
                  }}
                  onKeyDown={onKeyDown}
                  placeholderText={t('pages.nux.family.dateTimes.startTime')}
                  timeCaption={t('pages.nux.family.dateTimes.time')}
                  value={familyRegistration.startTimeDisplay}
                />
              </div>
              <div className='flex w-1/2'>
                <div className={dayLabelClasses}>
                  {getEndDay()}
                </div>
                <TimePicker
                  maxTime={getMaxEndTime()}
                  minTime={getMinEndTime(startDate, familyRegistration.startTime, endDate)}
                  onChange={(time: Date): void => validateTimeChange(time, true)}
                  onFocus={(e: FocusEvent<HTMLInputElement>): void => e.target.blur()}
                  onInputClick={(): void => {
                    tracker.track('clicked-button', {
                      elementText: t('pages.nux.family.dateTimes.endTime'),
                    });
                  }}
                  onKeyDown={onKeyDown}
                  placeholderText={t('pages.nux.family.dateTimes.endTime')}
                  timeCaption={t('pages.nux.family.dateTimes.time')}
                  value={familyRegistration.endTimeDisplay}
                />
              </div>
            </div>
            {!isOvernightEnabled && (
              <div className='text-right mr-4 pt-2 pb-12'>
                <Button
                  buttonType={ButtonType.tertiary}
                  className='underline'
                  onClick={(): void => enableOvernightOption()}>
                  {t('pages.nux.family.dateTimes.specific.overnightOption')}
                </Button>
              </div>
            )}
          </div>
        )}
        {!!startDate && isRecurring && (
          <div>
            <div className='grid grid-cols-7 gap-2'>
              {daysOfWeek.map((day: DayOfWeekOption) => (
                <span className='inline-block mb-6 w-1/7' key={day.code}>
                  <CustomCheckbox
                    disabled={isDayDisabled(day)}
                    labelPosition='top'
                    name={`RecurringDay-${day.id}`}
                    selected={isDaySelected(day)}
                    text={day.code}
                    toggle={(e: ChangeEvent<HTMLInputElement>): void => onDayOfWeekChange(e, day.id)}
                    widthClass='w-10 md:w-14'
                  />
                </span>
              ))}
            </div>
            <div className='mb-8'>
              <span className='text-md text-slate-500'>
                <label htmlFor='flexible-days-toggle'>
                  {t('pages.nux.family.dateTimes.recurring.flexibleDays')}
                </label>&nbsp;
                <ToggleSwitch
                  id='flexible-days-toggle'
                  className='w-5 h-5'
                  checked={!!recurring?.flexible}
                  handleClick={(): void => {
                    toggleFlexibleDays();
                    tracker.track('clicked-button', {
                      elementText: 'Days flexible',
                      elementId: recurring?.flexible || false,
                    });
                  }}
                />
              </span>
            </div>
            <Select
              id='select-time-of-day'
              label={t('pages.nux.family.dateTimes.recurring.selectTimeOfDay')}
              onChange={onTimeOfDaySelect}
              value={timeOfDaySelect?.id ?? -1}
            >
              {timesOfDay.map((td: TimeOfDayOption) => (
                <option key={td.code} value={td.id.toString()}>{td.label}</option>
              ))}
            </Select>
            <div className='mt-4 mb-8'>
              {isSpecificTime && daysOfWeek.map((day: DayOfWeekOption) => isDaySelected(day) && (
                <div className='w-full flex justify-center mt-4 space-x-4' key={`${day.code}-times`}>
                  <div className='flex'>
                    <div className={`${dayLabelClasses} w-[34px]`}>
                      {day.code}
                    </div>
                    <TimePicker
                      onChange={(date: Date): void => addRecurringTime(date, day.id, true)}
                      onInputClick={(): void => {
                        tracker.track('clicked-button', {
                          elementText: 'Recurring Start Time',
                        });
                      }}
                      placeholderText={t('pages.nux.family.dateTimes.startTime')}
                      timeCaption={t('pages.nux.family.dateTimes.time')}
                      timeFormat='h:mm aa'
                      value={getSpecificDayStartTime(day.id)}
                    />
                  </div>
                  <div className='flex'>
                    <TimePicker
                      maxTime={getMaxEndTime()}
                      minTime={getRecurringMinEndTime(day.id)}
                      onChange={(date: Date): void => addRecurringTime(date, day.id, false)}
                      onInputClick={(): void => {
                        tracker.track('clicked-button', {
                          elementText: 'Recurring End Time',
                        });
                      }}
                      placeholderText={t('pages.nux.family.dateTimes.endTime')}
                      timeCaption={t('pages.nux.family.dateTimes.time')}
                      timeFormat='h:mm aa'
                      value={getSpecificDayEndTime(day.id)}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </NuxContainer>
  )
});
